import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { AppButton, AppTextField } from "../../../components/form";
import {
  formatBytes,
  warehouseAdminService,
  type AdaptorUsageToday,
  type WarehouseExplainResult,
} from "../../../services/warehouseAdminService";

type Props = {
  adaptorId: string;
};

/**
 * "Test + EXPLAIN": guard check, scan estimate against the adaptor's limits and daily budget, and the engine plan,
 * without running the query.
 */
export default function WarehouseExplainPanel({ adaptorId }: Props) {
  const { t } = useTranslation();
  const [sql, setSql] = useState("SELECT 1");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WarehouseExplainResult | null>(null);
  const [usage, setUsage] = useState<AdaptorUsageToday | null>(null);

  useEffect(() => {
    warehouseAdminService
      .usageToday(adaptorId)
      .then(setUsage)
      .catch(() => setUsage(null));
  }, [adaptorId]);

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await warehouseAdminService.explain(adaptorId, sql));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h6">{t("adminDataAdaptors.explain.title")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("adminDataAdaptors.explain.help")}
      </Typography>
      {usage && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={t("adminDataAdaptors.explain.usageBytes", {
              used: formatBytes(usage.scannedBytes),
              limit: usage.dailyScanBytesLimit ? formatBytes(usage.dailyScanBytesLimit) : "∞",
            })}
          />
          <Chip
            size="small"
            label={t("adminDataAdaptors.explain.usageQueries", {
              used: usage.queries,
              limit: usage.dailyQueryLimit ?? "∞",
            })}
          />
        </Stack>
      )}
      <AppTextField
        label="SQL"
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        multiline
        minRows={4}
        maxRows={14}
        fullWidth
        inputProps={{ spellCheck: false, style: { fontFamily: "monospace", fontSize: 12 } }}
      />
      <Box>
        <AppButton variant="contained" onClick={() => void run()} disabled={running || !sql.trim()}>
          {running ? <CircularProgress size={18} /> : t("adminDataAdaptors.explain.run")}
        </AppButton>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {result && (
        <Stack spacing={1.5}>
          <Alert severity={result.wouldRun ? "success" : "warning"}>
            {result.wouldRun ? t("adminDataAdaptors.explain.wouldRun") : t("adminDataAdaptors.explain.wouldReject")}
          </Alert>
          {result.warnings.map((w) => (
            <Alert key={w} severity="warning">
              {w}
            </Alert>
          ))}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t("adminDataAdaptors.explain.estimatedBytes", { value: formatBytes(result.estimatedBytes) })}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t("adminDataAdaptors.explain.estimatedRows", {
                value: result.estimatedRows ?? "—",
              })}
            />
          </Stack>
          {result.plan && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: 360,
                overflow: "auto",
                fontSize: 12,
                bgcolor: "action.hover",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {result.plan}
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}
